import React from 'react';
import {
  Area,
  AreaChart,
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import type { AdminAnalyticsPayload } from '../contentConsoleApi';

type AnalyticsWorkspaceProps = {
  analytics: AdminAnalyticsPayload | null;
  loading: boolean;
  onRefresh: () => Promise<void>;
};

const formatNumber = (value: number) => new Intl.NumberFormat('zh-CN').format(value);

const ChartCard: React.FC<{
  title: string;
  description: string;
  type: 'area' | 'line';
  data: Array<{ label: string; visits: number; unique_visitors: number }>;
}> = ({ title, description, type, data }) => {
  const latest = data.at(-1);
  const peak = Math.max(...data.map((item) => Math.max(item.visits, item.unique_visitors)), 0);

  return (
    <section className="content-console-section-card analytics-chart-card">
      <div className="content-console-section-head is-inline-head">
        <h3>{title}</h3>
        <p>{description}</p>
      </div>
      <div className="analytics-chart-meta">
        <div>
          <span>{'最新访问'}</span>
          <strong>{formatNumber(latest?.visits ?? 0)}</strong>
        </div>
        <div>
          <span>{'峰值'}</span>
          <strong>{formatNumber(peak)}</strong>
        </div>
      </div>
      <div className="analytics-line-chart-shell">
        {data.length > 0 ? (
          <ResponsiveContainer width="100%" height="100%">
            {type === 'area' ? (
              <AreaChart data={data} margin={{ top: 12, right: 8, left: -16, bottom: 0 }}>
                <defs>
                  <linearGradient id="analyticsAreaFill" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#d92d43" stopOpacity={0.28} />
                    <stop offset="100%" stopColor="#d92d43" stopOpacity={0.03} />
                  </linearGradient>
                </defs>
                <CartesianGrid stroke="rgba(19, 34, 56, 0.08)" strokeDasharray="3 3" />
                <XAxis dataKey="label" tick={{ fill: '#7c8ba1', fontSize: 12 }} minTickGap={24} />
                <YAxis tick={{ fill: '#7c8ba1', fontSize: 12 }} width={42} />
                <Tooltip />
                <Legend />
                <Area type="monotone" dataKey="visits" name="访问量" stroke="#d92d43" fill="url(#analyticsAreaFill)" strokeWidth={2.5} />
                <Line type="monotone" dataKey="unique_visitors" name="访客数" stroke="#132238" strokeWidth={2} dot={false} />
              </AreaChart>
            ) : (
              <LineChart data={data} margin={{ top: 12, right: 8, left: -16, bottom: 0 }}>
                <CartesianGrid stroke="rgba(19, 34, 56, 0.08)" strokeDasharray="3 3" />
                <XAxis dataKey="label" tick={{ fill: '#7c8ba1', fontSize: 12 }} minTickGap={24} />
                <YAxis tick={{ fill: '#7c8ba1', fontSize: 12 }} width={42} />
                <Tooltip />
                <Legend />
                <Line type="monotone" dataKey="visits" name="访问量" stroke="#d92d43" strokeWidth={2.5} dot={false} activeDot={{ r: 4 }} />
                <Line type="monotone" dataKey="unique_visitors" name="访客数" stroke="#132238" strokeWidth={2.5} dot={false} activeDot={{ r: 4 }} />
              </LineChart>
            )}
          </ResponsiveContainer>
        ) : (
          <div className="content-console-empty">{'暂无统计数据'}</div>
        )}
      </div>
    </section>
  );
};

const AnalyticsWorkspace: React.FC<AnalyticsWorkspaceProps> = ({ analytics, loading, onRefresh }) => {
  const overview = analytics?.overview;

  return (
    <div className="content-console-analytics-shell">
      <section className="content-console-section-card analytics-hero-card">
        <div className="content-console-panel-head analytics-hero-head">
          <div>
            <span className="content-console-panel-tag">ANALYTICS</span>
            <h2>{'网站访问统计'}</h2>
            <p>{'查看总访问量、独立访客、日趋势、月趋势，以及页面和地区排行。'}</p>
          </div>
          <div className="content-console-editor-actions">
            <button type="button" className="content-console-secondary-button" onClick={() => void onRefresh()} disabled={loading}>
              {loading ? '刷新中...' : '刷新统计'}
            </button>
          </div>
        </div>
        <div className="content-console-summary-grid is-four-column analytics-summary-grid">
          <article className="content-console-summary-card">
            <span>{'累计访问量'}</span>
            <strong>{formatNumber(overview?.total_visits ?? 0)}</strong>
            <p>{'站点累计 page view'}</p>
          </article>
          <article className="content-console-summary-card">
            <span>{'累计访客数'}</span>
            <strong>{formatNumber(overview?.unique_visitors ?? 0)}</strong>
            <p>{'按访客标识去重'}</p>
          </article>
          <article className="content-console-summary-card">
            <span>{'今日访问 / 访客'}</span>
            <strong>{formatNumber(overview?.today_visits ?? 0)}</strong>
            <p>{'今日访客 '}{formatNumber(overview?.today_unique_visitors ?? 0)}</p>
          </article>
          <article className="content-console-summary-card">
            <span>{'本月 / 本年访问'}</span>
            <strong>{formatNumber(overview?.current_month_visits ?? 0)}</strong>
            <p>{'本年累计 '}{formatNumber(overview?.current_year_visits ?? 0)}</p>
          </article>
        </div>
      </section>

      <div className="analytics-chart-grid">
        <ChartCard
          title={'近 30 日趋势'}
          description={'按天查看访问量和访客数变化。'}
          type="area"
          data={analytics?.daily_series ?? []}
        />
        <ChartCard
          title={'近 12 月趋势'}
          description={'按月查看全年流量走势。'}
          type="line"
          data={analytics?.monthly_series ?? []}
        />
      </div>

      <div className="analytics-ranking-grid">
        <section className="content-console-section-card">
          <div className="content-console-section-head is-inline-head">
            <h3>{'热门页面'}</h3>
            <p>{'当前访问量最高的页面。'}</p>
          </div>
          <div className="analytics-ranking-list">
            {(analytics?.top_pages ?? []).map((item, index) => (
              <article key={`${item.path}-${index}`} className="analytics-ranking-item">
                <div>
                  <strong>{item.page_title || item.path}</strong>
                  <span>{item.path}</span>
                </div>
                <div className="analytics-ranking-metrics">
                  <strong>{formatNumber(item.visits)}</strong>
                  <span>{'访客 '}{formatNumber(item.unique_visitors)}</span>
                </div>
              </article>
            ))}
            {!loading && (analytics?.top_pages?.length ?? 0) === 0 ? <div className="content-console-empty">{'暂无页面访问数据'}</div> : null}
          </div>
        </section>

        <section className="content-console-section-card">
          <div className="content-console-section-head is-inline-head">
            <h3>{'地区排行'}</h3>
            <p>{'优先使用 CDN 或反向代理透传的国家、地区、城市信息。'}</p>
          </div>
          <div className="analytics-ranking-list">
            {(analytics?.top_regions ?? []).map((item, index) => (
              <article key={`${item.label}-${index}`} className="analytics-ranking-item">
                <div>
                  <strong>{item.label}</strong>
                  <span>{[item.country, item.region, item.city].filter(Boolean).join(' / ') || '未知地区'}</span>
                </div>
                <div className="analytics-ranking-metrics">
                  <strong>{formatNumber(item.visits)}</strong>
                  <span>{'访客 '}{formatNumber(item.unique_visitors)}</span>
                </div>
              </article>
            ))}
            {!loading && (analytics?.top_regions?.length ?? 0) === 0 ? <div className="content-console-empty">{'暂无地区访问数据'}</div> : null}
          </div>
        </section>
      </div>
    </div>
  );
};

export default AnalyticsWorkspace;
